import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {Project} from "../models/project";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {Document, DocumentProps} from "../models/document";

export interface DocumentStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
}

export class DocumentStore extends AbstractStore {

    journalStore: JournalStore | undefined;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Document>();

    constructor(props: DocumentStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Document[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(Document.sortByName);
        return [ ...filtered];
    }

    loadItem(projectGuid: string): Promise<Document | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            // !! do not use loadItems here. Then it wouldn't be possible to sync the
            // observed properties without swapping the whole items!!
            this.queryFeatures(undefined, [projectGuid]).then((documents: Document[]) => {
                if (documents[0] && documents[0].guid === projectGuid) {
                    const document = documents[0];
                    const localDocument = this.items.get(projectGuid);
                    if (localDocument) {
                        // return document as long as merging process is running. document can be displayed with
                        // indicator that syncing is in process
                        // TODO: merge document with already loaded document
                    }
                    this.addItem(document);
                    this.setSelectedItem(document);
                    this.setDataStatus("loaded");

                    resolve(document);
                } else {
                    reject();
                    throw new Error("Partial order not found");
                }
            }).catch((error) => {
                console.error(error);
                this.setDataStatus("error");
                throw new Error("Error loading document");
            });
        });
    }

    /**
     * Request all documents from server and store in local store
     *
     * @param projectId
     * @param guids (Optional) IDs of documents
     */
    loadItems(projectId?: number, guids?: string[]): Promise<Document[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectId, guids).then((documents: Document[]) => {
                    if (documents) {
                        // do only save to mobx when not requested with specified ids or contacts
                        const map = new Map<string, Document>();
                        documents.forEach((es: Document) => {
                            map.set(es.guid, es);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(documents);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting document.");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param projectId GUID of the related project
     * @param user
     */
    newItem(projectId: number, user: string): Promise<Document | null> {
        return new Promise((resolve, reject) => {

            if (!projectId) {
                reject();
            }

            // generate random project id
            const randomIds = new Uint8Array(1);
            self.crypto.getRandomValues(randomIds);

            const document = new Document({
                guid: crypto.randomUUID(),
                projectId,
                creator: user
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                document.feature = this.createFeature(null, document);

                // workaround: add missing prototype attributes
                Document.prototypeAttributes.forEach((attrName) => {
                    if (!document.feature.attributes[attrName]) {
                        document.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(document);
                this.setSelectedItem(document);

                resolve(document);
            });
        });
    }

    /**
     * Save document to the server
     *
     * @param document
     * @param updated
     * @param user
     */
    save(document: Document | null,
         updated: any,
         user: string): Promise<Document | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!document?.guid) {
                    reject("Document GUID undefined when saving edits.");
                    return;
                }

                if (document.isNewRecord) {
                    document.feature = this.createFeature(null, document);

                    // log creation in journal
                    this.journalStore?.journalizeItem(
                        document,
                        "document",
                        "add",
                        user,
                        [],
                        "Neues technisches Dokument"
                    );
                }

                if(!document.isNewRecord) {
                    // log changes to journal
                    let diffs: Difference[] = [];
                    diffs = this.compareAttributes(document, updated, document.comparableAttributes);

                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        document,
                        "document",
                        "update",
                        user,
                        diffs,
                        "Technische Dokumentation geändert"
                    );
                }

                const feature = document?.feature;
                if (!feature) {
                    reject(`Feature undefined for document ${document.objectId}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = Document.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for document ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = document.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(document, attrs);
                    if (document.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        document.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(document);
                }, (error) => {
                    console.error("Error saving document", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete an document
     *
     * @param project
     * @param document
     * @param user
     */
    delete(project: Project,
           document: Document | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project) {
                    reject("Project undefined when deleting document.");
                    return;
                }
                if (!document) {
                    reject("Partial order undefined when deleting.");
                    return;
                }

                if (document.isNewRecord) {
                    reject("This document is not saved. Does not have to be deleted.");
                    return;
                }

                // do not allow deleting when process is further than a specified step
                if (!project.isAlterable) {
                    reject("This project cannot be changed. Process is running.");
                    return;
                }

                const feature = document?.feature;
                if (!feature) {
                    reject(`Feature undefined for document ${document.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    this.resetSelectedItem();
                    this.removeItem(document);

                    // log deletion in journal
                    const nameDiff = new Difference({p: "name", o: document.fileName || ""});
                    const idDiff = new Difference({p: "guid", o: document.guid});
                    this.journalStore?.journalizeItem(
                        project,
                        "document",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Technische Dokumentation gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectId?: number, documentGuids?: string[]): Promise<Document[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                if (projectId !== undefined) {
                    queryParams.where += ` AND PROJ_ID = ${projectId}`;
                }
                // load all features if no project ID is specified
                if (documentGuids !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND GUID IN ('${documentGuids.join("','")}')`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const documents = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(documents);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Document[] {

        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const documents: Document[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Document);
            const document = new Document(json as DocumentProps);
            document.feature = feature;

            documents.push(document);
        });
        return documents;
    }

}
